/** The Tour tab (plan §1 "Body"): the sections in registry order, Launch & mapping first
 *  (across both columns when there are two — its tables want the width).
 *
 *  The layout follows the width the panels have in EDIT mode, in both modes (the records
 *  rule — entering edit mode re-lays nothing out): from 1100 px of content the dock
 *  (`PREVIEW_DOCK_WIDTH`, 380 px) sits beside the panels and takes its column out of that width; two panel
 *  columns only from 1200 px of panel width, else one in registry order. Each panel's
 *  field grid is pinned to its edit-mode column count (232 px minimum, 16 px gutters). */
import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import { FIELD_GRID_VAR, fieldGridColumns } from '@/features/records/panels/fieldGrid';
import { LEDGER_GAP_PX, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import { PREVIEW_DOCK_WIDTH } from '@/features/records/preview/PreviewDock';

import { CreateDepartureNow } from '../panels/CreateDepartureNow';
import { GuestJourneyPanel } from '../panels/GuestJourneyPanel';
import { LaunchMappingPanel } from '../panels/LaunchMappingPanel';
import { OperatorDefaultsNote } from '../panels/OperatorDefaultsNote';
import { TourSectionPanel } from '../panels/TourSectionPanel';
import { TOUR_SECTIONS, type TourSectionId } from '../registry';

/** Two panel columns from this much panel width. */
export const TOUR_TWO_COLUMNS_FROM_PX = 1200;
const PANEL_GAP_PX = 16;

export interface TourColumns {
  docked: boolean;
  /** The panels' width in edit mode. */
  editWidth: number;
  columns: 1 | 2;
}

/** @param width the page content's width (dock included); 0 = unmeasured. */
export function tourColumns(width: number): TourColumns {
  if (width === 0) return { docked: true, editWidth: 0, columns: 1 };
  const docked = width >= SHEET_BELOW_PX;
  const editWidth = docked ? width - PREVIEW_DOCK_WIDTH - LEDGER_GAP_PX : width;
  return { docked, editWidth, columns: editWidth >= TOUR_TWO_COLUMNS_FROM_PX ? 2 : 1 };
}

/** Two columns: Launch & mapping and Guest journey (their tables want the width) span
 *  both; Basics pairs with Schedule & departures and Content between them. */
const LEFT: TourSectionId[] = ['basics'];
const RIGHT: TourSectionId[] = ['schedule', 'content'];

export function TourTabView({
  width,
  afterGroup,
}: {
  width: number;
  /** Under a group's fields, by group id. */
  afterGroup?: Partial<Record<string, ReactNode>>;
}) {
  const { editWidth, columns } = tourColumns(width);
  const two = columns === 2;
  const pin = (panelWidth: number): Record<string, string> =>
    width > 0 ? { [FIELD_GRID_VAR]: fieldGridColumns(panelWidth) } : {};
  const byId = new Map(TOUR_SECTIONS.map((s) => [s.id, s]));
  const panel = (id: TourSectionId) => {
    if (id === 'launch') return <LaunchMappingPanel key={id} />;
    if (id === 'guest') return <GuestJourneyPanel key={id} />;
    const section = byId.get(id);
    if (!section) return null;
    const extra: Partial<Record<string, ReactNode>> = { ...(afterGroup ?? {}) };
    if (id === 'basics') extra.chat = <OperatorDefaultsNote />;
    return (
      <TourSectionPanel
        key={id}
        section={section}
        afterGroup={extra}
        {...(id === 'schedule' ? { groupAction: { departures: <CreateDepartureNow /> } } : {})}
      />
    );
  };
  const column = (ids: TourSectionId[], vars: Record<string, string>) => (
    <Box sx={{ display: 'grid', gap: `${PANEL_GAP_PX}px`, minWidth: 0, alignContent: 'start', ...vars }}>
      {ids.map(panel)}
    </Box>
  );
  return (
    <Box
      data-tour-tab
      data-columns={two ? 2 : 1}
      sx={{
        display: 'grid',
        gap: `${PANEL_GAP_PX}px`,
        gridTemplateColumns: two ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
        alignItems: 'start',
      }}
    >
      {two ? (
        <>
          <Box sx={{ gridColumn: '1 / -1', minWidth: 0, ...pin(editWidth) }}>{panel('launch')}</Box>
          {column(LEFT, pin((editWidth - PANEL_GAP_PX) / 2))}
          {column(RIGHT, pin((editWidth - PANEL_GAP_PX) / 2))}
          <Box sx={{ gridColumn: '1 / -1', minWidth: 0, ...pin(editWidth) }}>{panel('guest')}</Box>
        </>
      ) : (
        column(
          TOUR_SECTIONS.map((s) => s.id),
          pin(editWidth),
        )
      )}
    </Box>
  );
}
